/**
 * SAN-1473 (EX-06, BRD W-01 annotation 5) — why each missing profile item is
 * worth filling. Keys are the backend `missingFields` messages
 * (…/profile_completeness, e.g. startup.repository getStartupProfileCompletenessReport);
 * unknown messages fall back to their section. `rank` orders the dashboard's
 * top 3: fields that feed investor search and recommended-investor matching
 * (startup.repository searchStartupOrLiveDeal, investor.repository
 * getRecommendedInvestors) come first. Flag-gated by the callers
 * (notification_centre_enabled).
 */
interface GapBenefit {
  text: string;
  rank: number;
}

const FIELD_BENEFITS: { [message: string]: GapBenefit } = {
  'Industries are missing': { text: 'Investors filter by sector, and recommended investors are matched on your industries.', rank: 100 },
  'Business models are missing': { text: 'Investors filter by business model, and recommended investors are matched on it.', rank: 95 },
  'Technologies are missing': { text: 'Lets investors searching your technology find you.', rank: 90 },
  'Product stage is missing': { text: 'Puts you in stage-filtered investor searches.', rank: 85 },
  'Funding stage is missing': { text: 'Investors filter by funding stage when looking for deals.', rank: 80 },
  'Revenue stage is missing': { text: 'Investors filter by revenue stage.', rank: 75 },
  'Instruments are missing': { text: 'Shows investors which instruments you accept, so the right ones reach out.', rank: 70 },
  'Country is missing': { text: 'Puts you in location-filtered searches.', rank: 65 },
  'Target fundraise is missing': { text: 'Investors see how much you are raising before they connect.', rank: 60 },
  'Pitch Deck is missing': { text: 'Investors who open your profile can review your deck straight away.', rank: 55 },
  'Elevator pitch is missing': { text: 'Your one-line pitch is the first thing people read in search results.', rank: 50 },
  'Video Pitch is missing': { text: 'A video pitch is needed before you can request approval.', rank: 45 },
  'Video Pitch is not completed': { text: 'A video pitch is needed before you can request approval.', rank: 45 },
  'Founder name is missing': { text: 'Investors and mentors want to know who is behind the company.', rank: 40 },
  'Company name is missing': { text: 'Your company name is what people search for.', rank: 35 },
  'Company logo is missing': { text: 'Profiles with a logo are easier to recognise in lists and search.', rank: 30 },
};

const SECTION_BENEFITS: { [sectionKey: string]: GapBenefit } = {
  startupInformation: { text: 'A complete company profile is needed to request approval and appear in search.', rank: 25 },
  productInformation: { text: 'Product details help investors and partners understand what you build.', rank: 25 },
  financialInformation: { text: 'Fundraising details help investors decide whether to connect.', rank: 25 },
  industryTechnologyBusiness: { text: 'These details decide which investor searches you appear in.', rank: 25 },
  pitch_deck: { text: 'Your pitch is what investors review before reaching out.', rank: 25 },
  supportingDocument: { text: 'Required documents are needed before you can request approval.', rank: 20 },
  investorInformation: { text: 'Startups and admins see this when they review your profile.', rank: 25 },
  representativeInformation: { text: 'Startups know who to talk to at your organisation.', rank: 20 },
  investmentInformation: { text: 'Your investment focus decides which startups are recommended to you.', rank: 30 },
  corporateInformation: { text: 'A complete profile is needed to request approval and appear in search.', rank: 25 },
  mentorInformation: { text: 'Startups look for mentors by expertise; a complete profile gets you session requests.', rank: 25 },
  serviceProviderInformation: { text: 'Startups look for providers by service; a complete profile gets you found.', rank: 25 },
  programOfficeMemberInformation: { text: 'A complete profile is needed to request approval.', rank: 20 },
  individualInformation: { text: 'A complete profile is needed to request approval and connect with others.', rank: 20 },
  custom_forms: { text: 'Your organisation asks for this before approval.', rank: 15 },
};

/** The benefit of filling one missing item, or null when none is known. */
export function profileGapBenefit(message: string, sectionKey: string): GapBenefit | null {
  return FIELD_BENEFITS[message] || SECTION_BENEFITS[sectionKey] || null;
}
